import { InputHTMLAttributes, KeyboardEvent, ReactNode, useEffect, useRef, useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { AlertCircle, Eye, EyeOff } from "lucide-react";
import { Input } from "components/ui/Input";

// Shared pieces of the auth pages built on AuthSplit (login, register).

export const storeSession = (data: any) => {
    localStorage.setItem("token", data.token);
    localStorage.setItem("name", data.fullName ?? "");
    localStorage.setItem("id", data._id);
    localStorage.setItem("isAdmin", data.isAdmin);
    localStorage.setItem("isSub", data.isSub ?? false);
    localStorage.setItem("trialEndsAt", data.trialEndsAt ?? "");
    localStorage.removeItem("waiting-checkout");
};

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const linkClass =
    "rounded-sm font-medium text-ink underline-offset-4 transition-colors duration-150 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

const errorInputClass = "border-danger focus-visible:border-danger focus-visible:ring-danger";

export function AuthHeading({ title, subtitle }: { title: string; subtitle: string }) {
    return (
        <div className="mb-8 space-y-2">
            <h1 className="font-display text-3xl font-normal leading-tight tracking-tight text-ink">{title}</h1>
            <p className="text-sm text-ink-muted">{subtitle}</p>
        </div>
    );
}

// The Google button is an iframe with a fixed pixel width: measure the column so it matches
// the inputs at every breakpoint (Google caps it at 400px).
export function GoogleButton({ onToken, onError }: { onToken: (token: string) => void; onError: () => void }) {
    const ref = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(0);
    useEffect(() => {
        if (!ref.current) return;
        const observer = new ResizeObserver(([entry]) => setWidth(Math.min(400, Math.floor(entry.contentRect.width))));
        observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={ref} className="flex h-11 w-full justify-center">
            {width > 0 && (
                <GoogleLogin
                    onSuccess={(credentialResponse: any) => onToken(credentialResponse.credential)}
                    onError={onError}
                    width={width}
                    shape="rectangular"
                    size="large"
                    theme="outline"
                    logo_alignment="center"
                    text="continue_with"
                    locale="fr"
                />
            )}
        </div>
    );
}

export function AuthDivider() {
    return (
        <div className="my-6 flex items-center gap-3" role="separator">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs font-semibold uppercase tracking-wide text-ink-faint">ou avec votre email</span>
            <div className="h-px flex-1 bg-border" />
        </div>
    );
}

export function FormAlert({ children }: { children: ReactNode }) {
    return (
        <div role="alert" className="mb-4 flex items-start gap-2 rounded-lg border border-danger/20 bg-danger-subtle px-3 py-3 text-sm text-danger">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <p>{children}</p>
        </div>
    );
}

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
    id: string;
    label: string;
    error?: string | null;
    hint?: string;
    labelAside?: ReactNode;
}

export function Field({ id, label, error, hint, labelAside, className = "", ...props }: FieldProps) {
    const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
    return (
        <div>
            <div className="mb-1.5 flex items-center justify-between">
                <label htmlFor={id} className="text-sm font-medium text-ink">
                    {label}
                </label>
                {labelAside}
            </div>
            <Input
                id={id}
                aria-invalid={!!error}
                aria-describedby={describedBy}
                className={`${error ? errorInputClass : ""} ${className}`}
                {...props}
            />
            {error ? (
                <p id={`${id}-error`} className="mt-1.5 text-xs text-danger">
                    {error}
                </p>
            ) : hint ? (
                <p id={`${id}-hint`} className="mt-1.5 text-xs text-ink-muted">
                    {hint}
                </p>
            ) : null}
        </div>
    );
}

interface PasswordFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
    id: string;
    label: string;
    error?: string | null;
    labelAside?: ReactNode;
}

// Password input with a show/hide toggle and a Caps Lock warning.
export function PasswordField({ id, label, error, labelAside, ...props }: PasswordFieldProps) {
    const [visible, setVisible] = useState(false);
    const [capsLock, setCapsLock] = useState(false);
    const trackCapsLock = (e: KeyboardEvent<HTMLInputElement>) => setCapsLock(e.getModifierState("CapsLock"));
    const describedBy = [error && `${id}-error`, capsLock && `${id}-capslock`].filter(Boolean).join(" ") || undefined;

    return (
        <div>
            <div className="mb-1.5 flex items-center justify-between">
                <label htmlFor={id} className="text-sm font-medium text-ink">
                    {label}
                </label>
                {labelAside}
            </div>
            <div className="relative">
                <Input
                    id={id}
                    type={visible ? "text" : "password"}
                    aria-invalid={!!error}
                    aria-describedby={describedBy}
                    className={`pr-11 ${error ? errorInputClass : ""}`}
                    onKeyUp={trackCapsLock}
                    onKeyDown={trackCapsLock}
                    onBlur={() => setCapsLock(false)}
                    {...props}
                />
                <button
                    type="button"
                    onClick={() => setVisible((v) => !v)}
                    aria-label={visible ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    aria-pressed={visible}
                    className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-lg text-ink-faint transition-colors duration-150 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                    {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
            </div>
            {error && (
                <p id={`${id}-error`} className="mt-1.5 text-xs text-danger">
                    {error}
                </p>
            )}
            {capsLock && (
                <p id={`${id}-capslock`} className="mt-1.5 text-xs text-warning">
                    Verrouillage des majuscules activé.
                </p>
            )}
        </div>
    );
}
